/**
 * Provider Services Hooks (New Backend)
 *
 * Read + write the set of services a provider is authorized to offer.
 * Writing this set is what gates dispatch — the backend only sends a provider
 * order dispatches for services enabled here.
 *
 * Follows the same pattern as useProviderActions / useServiceActions, except the
 * toast strings go through t() rather than being hardcoded English.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getProviderServices, setProviderServices, toProviderServiceError } from '@/api/providerServices'
import { dashboardKeys } from '@/hooks/useDashboardStats'
import { useLocalization } from '@/contexts/LocalizationContext'
import type { ApiError } from '@/api/errorHandler'

/**
 * Fetch a provider's currently authorized services.
 *
 * @param providerUuid raw UUID (decode Relay global IDs before calling)
 * @param enabled      skip the request until the caller is ready (e.g. dialog closed)
 */
export function useProviderServices(providerUuid?: string | null, enabled = true) {
  const query = useQuery({
    queryKey: ['providerServices', providerUuid],
    queryFn: async () => {
      try {
        const res = await getProviderServices(providerUuid as string)
        return res.services ?? []
      } catch (err) {
        // Normalize so consumers can branch on ApiError.code / .status
        // (403 PERMISSION_DENIED, 404 PROVIDER_NOT_FOUND) rather than digging into axios.
        throw toProviderServiceError(err)
      }
    },
    enabled: !!providerUuid && enabled,
    staleTime: 1000 * 30,
    retry: false, // a 403/404 here is a real answer, not a blip worth retrying
  })

  return {
    services: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as ApiError | Error | null,
    refetch: query.refetch,
  }
}

/** Mutation for replacing a provider's authorized service set. */
export function useProviderServiceActions() {
  const queryClient = useQueryClient()
  const { t } = useLocalization()

  const invalidateAll = async (providerUuid: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['providerServices', providerUuid] }),
      queryClient.invalidateQueries({ queryKey: ['organizationProviders'] }),
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
    ])
  }

  const setServicesMutation = useMutation({
    mutationFn: async ({
      providerUuid,
      serviceTemplateIds,
    }: {
      providerUuid: string
      serviceTemplateIds: string[]
    }) => {
      try {
        const res = await setProviderServices(providerUuid, serviceTemplateIds)
        if (res?.success === false) {
          throw new Error(t('providerServices.saveFailed'))
        }
        return res
      } catch (err) {
        throw toProviderServiceError(err)
      }
    },
    onSuccess: async (_data, variables) => {
      toast.success(t('providerServices.savedSuccess'))
      await invalidateAll(variables.providerUuid)
    },
    onError: (err: Error) => {
      toast.error(t('providerServices.saveFailed'), { description: err?.message })
    },
  })

  return {
    setProviderServices: (providerUuid: string, serviceTemplateIds: string[]) =>
      setServicesMutation.mutateAsync({ providerUuid, serviceTemplateIds }).then(() => {}),
    setServicesMutation,
    isLoading: setServicesMutation.isPending,
  }
}
